import { useCallback, useEffect, useRef, useState } from 'react'

import type {
  OmniTableColumnChange,
  OmniTableColumnOption
} from './omni-table-column-editor'

export const OMNI_TABLE_PAGE_SIZES = [5, 10, 20, 30, 40, 50, 100] as const
const DB_NAME = 'omni-table-preferences'
const DB_VERSION = 1
const STORE_NAME = 'preferences'
const CHANGE_EVENT = 'acqua:omni-table-preferences'

function browserStorage(): IDBFactory | undefined {
  return typeof window === 'undefined' ? undefined : window.indexedDB
}

/**
 * Values this page has already read from or written to the browser's storage,
 * so a table that mounts again with the same keys (switching back to a table
 * tab, a remount after navigation) is ready at once. Kept per storage, and
 * absent without one.
 */
const caches = new WeakMap<IDBFactory, Map<string, string | null>>()
const emptyCache = new Map<string, string | null>()

function pageCache(): Map<string, string | null> {
  const factory = browserStorage()
  if (!factory) return emptyCache
  const cache = caches.get(factory) ?? new Map<string, string | null>()
  caches.set(factory, cache)
  return cache
}

/** Writes started per key, so a read that a newer write overtook is dropped. */
const writeCounts = new Map<string, number>()
const writesTo = (key: string) => writeCounts.get(key) ?? 0

function openDatabase(storage: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = storage.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME)
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function readStored(
  storage: IDBFactory,
  key: string
): Promise<string | null> {
  const db = await openDatabase(storage)
  try {
    return await new Promise((resolve, reject) => {
      const request = db
        .transaction(STORE_NAME, 'readonly')
        .objectStore(STORE_NAME)
        .get(key)
      request.onsuccess = () =>
        resolve(typeof request.result === 'string' ? request.result : null)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

async function writeStored(
  storage: IDBFactory,
  key: string,
  value: string | null
) {
  const db = await openDatabase(storage)
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, 'readwrite')
      const store = transaction.objectStore(STORE_NAME)
      if (value === null) store.delete(key)
      else store.put(value, key)
      transaction.oncomplete = () => resolve()
      transaction.onabort = () => reject(transaction.error)
      transaction.onerror = () => reject(transaction.error)
    })
  } finally {
    db.close()
  }
}

/**
 * One channel per page: a BroadcastChannel never delivers to the object that
 * posted, so this page hears only other browser tabs.
 */
let otherTabs: BroadcastChannel | undefined
const otherTabListeners = new Set<(key: string) => void>()

function otherTabsChannel() {
  if (typeof BroadcastChannel === 'undefined') return undefined
  if (!otherTabs) {
    otherTabs = new BroadcastChannel(CHANGE_EVENT)
    otherTabs.onmessage = (event: MessageEvent<string>) =>
      otherTabListeners.forEach(listener => listener(event.data))
  }
  return otherTabs
}

/** Tells the other tables in this page that a key changed. */
function announceInPage(key: string) {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: key }))
}

function useStoredValue(
  key: string,
  onWritten: ((saved: boolean, startedAt: number) => void) | undefined,
  persist: boolean
) {
  // Not persisted: as with no browser storage, nothing is read or written.
  const storageFor = () => (persist ? browserStorage() : undefined)
  const cacheFor = () => (persist ? pageCache() : emptyCache)
  const [stored, setStored] = useState<{
    key: string
    value: string | null
    ready: boolean
  }>(() => ({
    key,
    value: cacheFor().get(key) ?? null,
    ready: cacheFor().has(key)
  }))
  const onWrittenRef = useRef(onWritten)
  onWrittenRef.current = onWritten
  const [writeFailed, setWriteFailed] = useState(false)

  useEffect(() => {
    let active = true
    const read = (fromCacheOnly = false) => {
      const cache = cacheFor()
      if (cache.has(key)) {
        setStored({ key, value: cache.get(key) ?? null, ready: true })
        if (fromCacheOnly) return
      }
      const storage = storageFor()
      if (!storage) {
        // Nothing to read: the defaults on mount, and a change this table
        // made stays in its state.
        if (!fromCacheOnly) setStored({ key, value: null, ready: true })
        return
      }
      const writesBefore = writesTo(key)
      void readStored(storage, key).then(
        value => {
          if (writesTo(key) !== writesBefore) return
          cacheFor().set(key, value)
          if (active) setStored({ key, value, ready: true })
        },
        () => {
          if (writesTo(key) !== writesBefore) return
          if (active) setStored({ key, value: null, ready: true })
        }
      )
    }
    const syncLocal = (event: Event) => {
      if ((event as CustomEvent<string>).detail === key) read(true)
    }
    const syncOtherTab = (changed: string) => {
      if (changed !== key) return
      // Another browser tab wrote this key; our cached copy is stale.
      cacheFor().delete(key)
      read()
    }
    otherTabsChannel()
    otherTabListeners.add(syncOtherTab)
    read()
    window.addEventListener(CHANGE_EVENT, syncLocal)
    return () => {
      active = false
      window.removeEventListener(CHANGE_EVENT, syncLocal)
      otherTabListeners.delete(syncOtherTab)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the helpers read `persist`
  }, [key, persist])

  const write = useCallback(
    (value: string | null): Promise<boolean> => {
      const startedAt = Date.now()
      writeCounts.set(key, writesTo(key) + 1)
      const cache = cacheFor()
      if (cache !== emptyCache) cache.set(key, value)
      setStored({ key, value, ready: true })
      announceInPage(key)
      const storage = storageFor()
      if (!storage) {
        onWrittenRef.current?.(false, startedAt)
        return Promise.resolve(false)
      }
      return writeStored(storage, key, value).then(
        () => {
          // Other browser tabs re-read the key, so they hear of it once stored.
          otherTabsChannel()?.postMessage(key)
          onWrittenRef.current?.(true, startedAt)
          return true
        },
        () => {
          setWriteFailed(true)
          // This table keeps the value for the visit; the page cache only
          // holds what the browser actually stored.
          if (cache.get(key) === value) cache.delete(key)
          onWrittenRef.current?.(false, startedAt)
          return false
        }
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the helpers read `persist`
    [key, persist]
  )

  return {
    value: stored.key === key ? stored.value : cacheFor().get(key) ?? null,
    ready: stored.key === key ? stored.ready : cacheFor().has(key),
    persistent: !!storageFor() && !writeFailed,
    write
  }
}

function parse(value: string | null): unknown {
  try {
    return value === null ? null : JSON.parse(value)
  } catch {
    return null
  }
}

function restoreColumns(
  value: unknown,
  defaults: readonly OmniTableColumnOption[]
): OmniTableColumnOption[] {
  if (
    !value ||
    typeof value !== 'object' ||
    !('version' in value) ||
    value.version !== 1 ||
    !('columns' in value) ||
    !Array.isArray(value.columns)
  ) {
    return [...defaults]
  }
  const remaining = new Map(defaults.map(column => [column.id, column]))
  const restored: OmniTableColumnOption[] = []
  for (const saved of value.columns as unknown[]) {
    if (
      !saved ||
      typeof saved !== 'object' ||
      !('id' in saved) ||
      typeof saved.id !== 'string' ||
      !('visible' in saved) ||
      typeof saved.visible !== 'boolean'
    )
      continue
    const column = remaining.get(saved.id)
    if (!column) continue
    restored.push({ ...column, visible: saved.visible })
    remaining.delete(saved.id)
  }
  return [...restored, ...remaining.values()]
}

export interface OmniTablePreferencesOptions {
  /** User/account + product identity; the tabs of one table may share it or keep their own. */
  pageSizeKey: string
  /** Scope to the user/account and column set, usually the table's tab. */
  columnsKey: string
  defaultColumns: readonly OmniTableColumnOption[]
  defaultPageSize?: (typeof OMNI_TABLE_PAGE_SIZES)[number]
  /**
   * False keeps the preferences in memory only: nothing is read from or written
   * to the browser, and a change lasts while the table stays mounted, as
   * without browser storage. For a session whose keys would not be its own.
   */
  persist?: boolean
  /** Called once a user change has been written to the browser, or failed to be. */
  onPersistence?: (
    outcome: 'saved' | 'failed',
    kind: 'page-size' | 'columns',
    startedAt: number
  ) => void
}

/** Optional browser persistence; the table itself remains fully controlled. */
export function useOmniTablePreferences({
  pageSizeKey,
  columnsKey,
  defaultColumns,
  defaultPageSize = 10,
  persist = true,
  onPersistence
}: OmniTablePreferencesOptions) {
  const size = useStoredValue(
    pageSizeKey,
    (saved, startedAt) =>
      onPersistence?.(saved ? 'saved' : 'failed', 'page-size', startedAt),
    persist
  )
  const view = useStoredValue(
    columnsKey,
    (saved, startedAt) =>
      onPersistence?.(saved ? 'saved' : 'failed', 'columns', startedAt),
    persist
  )
  const savedSize = parse(size.value)
  const pageSize =
    typeof savedSize === 'number' &&
    OMNI_TABLE_PAGE_SIZES.some(option => option === savedSize)
      ? savedSize
      : defaultPageSize
  // The order previewed while a column is dragged, over the stored view.
  const [preview, setPreview] = useState<string | null>(null)
  const columns = restoreColumns(parse(preview ?? view.value), defaultColumns)

  return {
    /** The page size and the column view are both known. */
    ready: size.ready && view.ready,
    pageSize,
    /** The page size in use is one the user picked, read back from this browser or chosen during this visit; it can equal the default. */
    hasStoredPageSize: typeof savedSize === 'number' && pageSize === savedSize,
    /**
     * Browser storage is available and none of this table's writes has failed
     * so far. Whether one change was stored is what `setPageSize` resolves to.
     */
    persistent: size.persistent && view.persistent,
    columns,
    isCustomized: columns.some(
      (column, index) =>
        column.visible !== defaultColumns[index]?.visible ||
        column.id !== defaultColumns[index]?.id
    ),
    /** Resolves to whether the browser stored the new page size. */
    setPageSize: (next: number): Promise<boolean> =>
      OMNI_TABLE_PAGE_SIZES.some(option => option === next)
        ? size.write(JSON.stringify(next))
        : Promise.resolve(false),
    /**
     * A `dragging` change only previews the order for the table to follow; it
     * is not stored and resolves to false. The drop stores it.
     */
    setColumns: (
      next: readonly OmniTableColumnOption[],
      change?: OmniTableColumnChange
    ): Promise<boolean> => {
      const value = JSON.stringify({
        version: 1,
        columns: next.map(({ id, visible }) => ({ id, visible }))
      })
      if (change?.dragging) {
        setPreview(value)
        return Promise.resolve(false)
      }
      setPreview(null)
      return view.write(value)
    },
    resetColumns: () => {
      setPreview(null)
      return view.write(null)
    }
  }
}
